/**
 * Successful Transactions — agent workspace.
 *
 * Mirrors the Transactions list but filtered to status=success and adds
 * agent-editable columns: username, "Credited to wallet" claim checkbox,
 * bonus value (₹), bonus percent (%), and a website (link) selector.
 *
 * Permission semantics (enforced server-side; UI just reflects):
 *   - read: list visible
 *   - write: inline edits + claim allowed
 *   - export_success feature flag: CSV download button visible
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Loader2, Search, Download, Check, X, Pencil, Lock } from "lucide-react";
import api, { fmtINR, formatDetail } from "@/lib/api";
import { buildWebsiteColorMap } from "@/lib/websiteColors";
import { useFieldLock } from "@/lib/fieldLock";

export default function SuccessfulTransactions() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [perms, setPerms] = useState({ can_write: false, can_export: false });
    const [q, setQ] = useState("");
    const [credited, setCredited] = useState("");        // ''|'yes'|'no'
    const [linkFilter, setLinkFilter] = useState("");
    // Date filter is preset-driven — the vast majority of agent workflows
    // are "today", "yesterday", or short recent windows. Custom-range
    // survives as an escape hatch. Rendering only two YYYY-MM-DD text
    // inputs (as the old UI did) forced the agent to know the exact
    // dates and typed a lot; the preset is a one-click filter.
    // Storage: `datePreset` drives the query params; dateFrom/dateTo are
    // still tracked because Custom uses them and the backend accepts them.
    const [datePreset, setDatePreset] = useState("today");
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");
    const [savingId, setSavingId] = useState(null);
    const [editing, setEditing] = useState({});           // {[txn_id]: {field: value}}
    const [err, setErr] = useState("");
    const [links, setLinks] = useState([]);
    const { isLocked, requireUnlock, ConfirmModal, lockedFields, sudoActive, hasAnyLocks, lock } =
        useFieldLock("partner.successful_transactions");

    // Resolve the current preset to an effective (from, to) pair.
    // Returned strings are ISO datetimes at IST-day boundaries (start of
    // the day = 00:00, end = 23:59:59). Kept in a helper so both the
    // list-load and the CSV-export URL use identical semantics.
    const resolveDateRange = useCallback(() => {
        if (datePreset === "all") return { from: "", to: "" };
        if (datePreset === "custom") return { from: dateFrom, to: dateTo };
        // Compute in IST (payment gateway is Indian-only) so "today" means
        // "today in IST", not UTC. `en-CA` locale gives YYYY-MM-DD.
        const nowIst = new Date(new Date().toLocaleString("en-US", { timeZone: "Asia/Kolkata" }));
        const startOfDay = (d) => {
            const s = new Date(d);
            s.setHours(0, 0, 0, 0);
            return s;
        };
        const endOfDay = (d) => {
            const e = new Date(d);
            e.setHours(23, 59, 59, 999);
            return e;
        };
        let from;
        let to = endOfDay(nowIst);
        if (datePreset === "today") {
            from = startOfDay(nowIst);
        } else if (datePreset === "yesterday") {
            const y = new Date(nowIst);
            y.setDate(y.getDate() - 1);
            from = startOfDay(y);
            to = endOfDay(y);
        } else if (datePreset === "7d") {
            const s = new Date(nowIst);
            s.setDate(s.getDate() - 6);
            from = startOfDay(s);
        } else if (datePreset === "30d") {
            const s = new Date(nowIst);
            s.setDate(s.getDate() - 29);
            from = startOfDay(s);
        } else {
            return { from: "", to: "" };
        }
        return { from: from.toISOString(), to: to.toISOString() };
    }, [datePreset, dateFrom, dateTo]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (q.trim()) params.set("q", q.trim());
            if (credited) params.set("credited", credited);
            if (linkFilter) params.set("link_code", linkFilter);
            const { from, to } = resolveDateRange();
            if (from) params.set("date_from", from);
            if (to) params.set("date_to", to);
            // Explicit 25s frontend timeout — must be LONGER than the
            // backend's 15s max_time_ms so we surface the backend's
            // specific error message ("Query took too long. Try..."),
            // and SHORTER than the gateway's 60s so we never see a raw
            // 504 for a query the backend already aborted.
            const r = await api.get(`/partner/success-transactions?${params}`, { timeout: 25000 });
            setItems(r.data.items || []);
            setPerms({ can_write: !!r.data.can_write, can_export: !!r.data.can_export });
            setErr("");
        } catch (e) {
            // Distinguish helpful backend messages from generic gateway
            // failures. A 504/timeout means the backend held the socket
            // open past the gateway's tolerance; a 503 with a specific
            // detail is our own throttle/hint. Both are actionable, so
            // we surface the backend message when available and
            // fall back to a plain-English hint when the browser just
            // saw "Gateway Timeout".
            const status = e?.response?.status;
            const detail = formatDetail(e?.response?.data?.detail);
            if (status === 504 || e?.code === "ECONNABORTED") {
                setErr(
                    "The server is taking too long — try a smaller date range (Today or Yesterday), " +
                    "or refresh in a minute if a deploy is currently rolling out."
                );
            } else if (detail) {
                setErr(detail);
            } else {
                setErr(e?.message || "Something went wrong.");
            }
        } finally { setLoading(false); }
    }, [q, credited, linkFilter, resolveDateRange]);

    // `load` closes over paging state so we memoize with useCallback to
    // keep the useEffect dep-array honest and avoid stale-closure bugs
    // when the eventual pagination lands. Guards against the lint rule
    // `react-hooks/exhaustive-deps` firing on empty [] arrays.
    useEffect(() => { load(); }, [load]);

    // Pull payment links once for the website filter / per-row dropdown.
    useEffect(() => {
        (async () => {
            try {
                const r = await api.get("/partner/payment-links");
                setLinks(r.data.items || []);
            } catch (e) { console.warn("payment-links fetch failed:", e); }
        })();
    }, []);

    const linkOptions = useMemo(() => {
        const opts = links.map((l) => ({ code: l.link_code, name: l.display_name || l.link_code }));
        // Include any link_codes referenced in items but not in the links list.
        const seen = new Set(opts.map((o) => o.code));
        items.forEach((t) => {
            if (t.link_code && !seen.has(t.link_code)) {
                opts.push({ code: t.link_code, name: t.link_display_name || t.link_code });
                seen.add(t.link_code);
            }
        });
        return opts;
    }, [links, items]);

    // Per-website colour map. Computed once per item-list refresh; each
    // visible website gets a guaranteed-unique palette slot (by sorted
    // position). Lookup is O(1) inside the render loop below.
    const colorOf = useMemo(() => buildWebsiteColorMap(items), [items]);

    const startEdit = (txnId, field, currentValue) => {
        if (!perms.can_write) return;
        const doStart = () => setEditing((s) => {
            const t = items.find((x) => x.txn_id === txnId);
            // For the bonus pair, prime BOTH fields so saveField has the
            // counterpart even if the user only touches one input.
            if (field === "bonus_value" || field === "bonus_percent") {
                return {
                    ...s,
                    [txnId]: {
                        ...(s[txnId] || {}),
                        bonus_value: t?.bonus_value ?? "",
                        bonus_percent: t?.bonus_percent ?? "",
                    },
                };
            }
            return { ...s, [txnId]: { ...(s[txnId] || {}), [field]: currentValue ?? "" } };
        });
        // If this field is locked, gate behind master-code modal.
        if (isLocked(field) || isLocked("bonus_value") && (field === "bonus_value" || field === "bonus_percent")) {
            requireUnlock(doStart, { fields: [field] });
            return;
        }
        doStart();
    };
    const cancelEdit = (txnId, field) => {
        setEditing((s) => {
            const next = { ...(s[txnId] || {}) };
            delete next[field];
            return { ...s, [txnId]: next };
        });
    };
    const setEditValue = (txnId, field, value) => {
        setEditing((s) => {
            const cur = { ...(s[txnId] || {}), [field]: value };
            // Two-way bind: typing % updates ₹ and vice versa, anchored
            // on the txn's amount. Empty/0 amount → skip (would divide by 0).
            const t = items.find((x) => x.txn_id === txnId);
            const amount = Number(t?.amount || 0);
            if (amount > 0) {
                if (field === "bonus_value") {
                    const v = value === "" ? "" : parseFloat(value);
                    cur.bonus_percent = v === "" || Number.isNaN(v)
                        ? ""
                        : Number(((v / amount) * 100).toFixed(2));
                } else if (field === "bonus_percent") {
                    const p = value === "" ? "" : parseFloat(value);
                    cur.bonus_value = p === "" || Number.isNaN(p)
                        ? ""
                        : Number(((p / 100) * amount).toFixed(2));
                }
            }
            return { ...s, [txnId]: cur };
        });
    };

    const saveField = async (txnId, field) => {
        const ed = editing[txnId] || {};
        const raw = ed[field];
        const payload = {};
        if (field === "customer_username") payload.customer_username = String(raw || "").trim();
        if (field === "bonus_value" || field === "bonus_percent") {
            // Save BOTH bonus_value + bonus_percent together so the
            // computed counterpart from the two-way bind is persisted.
            const v = ed.bonus_value;
            const p = ed.bonus_percent;
            payload.bonus_value = v === "" || v == null ? 0 : parseFloat(v);
            payload.bonus_percent = p === "" || p == null ? 0 : parseFloat(p);
        }
        if (field === "link_code") payload.link_code = raw || null;
        if (field === "customer_username" && (!payload.customer_username || payload.customer_username.length < 1)) {
            setErr("Username cannot be empty"); return;
        }
        setSavingId(txnId);
        try {
            const r = await api.patch(`/partner/success-transactions/${txnId}`, payload);
            const fresh = r.data.transaction;
            setItems((arr) => arr.map((t) => (t.txn_id === txnId ? fresh : t)));
            if (field === "bonus_value" || field === "bonus_percent") {
                cancelEdit(txnId, "bonus_value");
                cancelEdit(txnId, "bonus_percent");
            } else {
                cancelEdit(txnId, field);
            }
            setErr("");
        } catch (e) {
            setErr(formatDetail(e?.response?.data?.detail) || e.message);
        } finally { setSavingId(null); }
    };

    const toggleCredited = async (t) => {
        if (!perms.can_write) return;
        const doToggle = async () => {
            setSavingId(t.txn_id);
            try {
                const r = await api.patch(`/partner/success-transactions/${t.txn_id}`, {
                    credited_to_wallet: !t.credited_to_wallet,
                });
                const fresh = r.data.transaction;
                setItems((arr) => arr.map((x) => (x.txn_id === t.txn_id ? fresh : x)));
            } catch (e) {
                if (e?.response?.status === 423) {
                    requireUnlock(() => doToggle(), { fields: ["credited_to_wallet"] });
                    return;
                }
                setErr(formatDetail(e?.response?.data?.detail) || e.message);
            } finally { setSavingId(null); }
        };
        if (isLocked("credited_to_wallet")) {
            requireUnlock(doToggle, { fields: ["credited_to_wallet"] });
            return;
        }
        doToggle();
    };

    const downloadCsv = async () => {
        const params = new URLSearchParams();
        if (q.trim()) params.set("q", q.trim());
        if (credited) params.set("credited", credited);
        if (linkFilter) params.set("link_code", linkFilter);
        const { from, to } = resolveDateRange();
        if (from) params.set("date_from", from);
        if (to) params.set("date_to", to);
        try {
            const r = await api.get(`/partner/success-transactions/export?${params}`, { responseType: "blob" });
            const url = URL.createObjectURL(r.data);
            const a = document.createElement("a");
            a.href = url; a.download = "success_transactions.csv";
            document.body.appendChild(a); a.click();
            document.body.removeChild(a); URL.revokeObjectURL(url);
        } catch (e) {
            setErr(formatDetail(e?.response?.data?.detail) || e.message);
        }
    };

    return (
        <div className="space-y-4" data-testid="success-txns-page">
            <ConfirmModal />
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h1 className="text-xl font-bold tracking-tight">Successful Transactions</h1>
                    <p className="text-xs text-slate-500 mt-0.5">Agent workspace — claim payouts, track bonus payable, review credit-to-wallet status.</p>
                </div>
                <div className="flex items-center gap-2">
                    {hasAnyLocks && (
                        <button type="button" onClick={lock} data-testid="success-relock"
                                title={sudoActive ? "Click to re-lock" : "Edits to bonus/credited fields require a master code"}
                                className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs border ${sudoActive ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>
                            <Lock className="w-3.5 h-3.5" /> {sudoActive ? "Unlocked (30 min)" : "Locked"}
                        </button>
                    )}
                    {perms.can_export && (
                        <button data-testid="success-export-btn" type="button" onClick={downloadCsv}
                                className="btn-secondary inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-sm">
                            <Download className="w-4 h-4" /> Export CSV
                        </button>
                    )}
                </div>
            </div>

            {err && <div data-testid="success-err" className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">{err}</div>}

            {/* Filter bar */}
            <div className="card p-3 flex flex-wrap items-end gap-2.5">
                <div className="flex-1 min-w-[200px]">
                    <label className="label">Search</label>
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                        <input data-testid="success-search" value={q} onChange={(e) => setQ(e.target.value)}
                               onKeyDown={(e) => e.key === "Enter" && load()}
                               className="input !pl-10" placeholder="Username, UTR, txn id…" />
                    </div>
                </div>
                <div>
                    <label className="label">Credited</label>
                    <select data-testid="success-credited-filter" value={credited} onChange={(e) => setCredited(e.target.value)} className="input min-w-[120px]">
                        <option value="">All</option>
                        <option value="yes">Credited</option>
                        <option value="no">Not credited</option>
                    </select>
                </div>
                <div>
                    <label className="label">Website</label>
                    <select data-testid="success-link-filter" value={linkFilter} onChange={(e) => setLinkFilter(e.target.value)} className="input min-w-[160px]">
                        <option value="">All</option>
                        {linkOptions.map((o) => <option key={o.code} value={o.code}>{o.name}</option>)}
                    </select>
                </div>
                <div>
                    <label className="label">Date</label>
                    <select
                        data-testid="success-date-preset"
                        value={datePreset}
                        onChange={(e) => setDatePreset(e.target.value)}
                        className="input min-w-[140px]"
                    >
                        <option value="today">Today</option>
                        <option value="yesterday">Yesterday</option>
                        <option value="7d">Last 7 days</option>
                        <option value="30d">Last 30 days</option>
                        <option value="all">All time</option>
                        <option value="custom">Custom range…</option>
                    </select>
                </div>
                {datePreset === "custom" && (
                    <>
                        <div>
                            <label className="label">From</label>
                            <input data-testid="success-from" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="input" />
                        </div>
                        <div>
                            <label className="label">To</label>
                            <input data-testid="success-to" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="input" />
                        </div>
                    </>
                )}
                <button data-testid="success-apply" type="button" onClick={load}
                        className="btn-primary px-4 py-2 rounded-md text-sm">
                    Apply
                </button>
                <button data-testid="success-clear" type="button"
                        onClick={() => { setQ(""); setCredited(""); setLinkFilter(""); setDatePreset("today"); setDateFrom(""); setDateTo(""); setTimeout(load, 0); }}
                        className="btn-secondary px-3 py-2 rounded-md text-sm">
                    Clear
                </button>
            </div>

            {/* Per-website colour legend — appears only when 2+ websites
                are in the current result set. Helps the agent learn the
                colour-to-website map at a glance instead of squinting at
                each pill. Each chip mirrors the row treatment (stripe +
                pill) so the visual language stays consistent. */}
            {(() => {
                const names = Array.from(new Set(items.map((t) => t.website_name).filter(Boolean))).sort((a, b) => a.localeCompare(b));
                if (names.length < 2) return null;
                return (
                    <div data-testid="website-color-legend" className="flex flex-wrap items-center gap-2 px-1 -mt-2 mb-1 text-[11px]">
                        <span className="text-slate-400 uppercase tracking-wider font-medium">Legend:</span>
                        {names.map((n) => {
                            const c = colorOf(n);
                            return (
                                <span key={n} className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md border font-semibold ${c.pill}`}>
                                    <span className="w-2 h-2 rounded-sm" style={{ backgroundColor: c.stripe }} />
                                    {c.initial && (
                                        <span className="font-mono text-[9px] tracking-wider opacity-70">
                                            {c.initial}
                                        </span>
                                    )}
                                    {n}
                                </span>
                            );
                        })}
                    </div>
                );
            })()}

            {/* Table */}
            <div className="card overflow-hidden">
                {loading ? (
                    <div className="p-8 flex items-center justify-center text-slate-400 text-sm">
                        <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading…
                    </div>
                ) : items.length === 0 ? (
                    <div className="p-8 text-center text-slate-400 text-sm" data-testid="success-empty">No successful transactions match your filters.</div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm" data-testid="success-table">
                            <thead className="bg-slate-50 border-b border-slate-200 text-[11px] uppercase tracking-wider text-slate-500">
                                <tr>
                                    <th className="text-left px-3 py-2 font-semibold">Txn / UTR</th>
                                    <th className="text-left px-3 py-2 font-semibold">Username</th>
                                    <th className="text-left px-3 py-2 font-semibold">Amount</th>
                                    <th className="text-left px-3 py-2 font-semibold">Link</th>
                                    <th data-testid="success-th-website" className="text-left px-3 py-2 font-semibold">Website</th>
                                    <th className="text-center px-3 py-2 font-semibold">Credited</th>
                                    <th className="text-left px-3 py-2 font-semibold">Bonus ₹</th>
                                    <th className="text-left px-3 py-2 font-semibold">Bonus %</th>
                                    <th className="text-left px-3 py-2 font-semibold">When</th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((t) => {
                                    const ed = editing[t.txn_id] || {};
                                    const saving = savingId === t.txn_id;
                                    const usernameEditing = "customer_username" in ed;
                                    const bonusVEditing = "bonus_value" in ed;
                                    const bonusPEditing = "bonus_percent" in ed;
                                    const linkEditing = "link_code" in ed;
                                    // Per-website colour — guaranteed-unique
                                    // (alphabetical position in the visible set
                                    // → palette slot). Three layers of visual
                                    // emphasis: row-tint + 6px left stripe +
                                    // chip-style pill in the Website column.
                                    const ws = colorOf(t.website_name);
                                    return (
                                        <tr key={t.txn_id}
                                            className="border-b border-slate-100 hover:brightness-95 transition-all"
                                            style={{ backgroundColor: ws.tint }}
                                            data-testid={`success-row-${t.txn_id}`}>
                                            <td className="px-3 py-2 font-mono text-[11px] border-l-[6px]" style={{ borderLeftColor: ws.stripe }}>
                                                <div className="text-slate-700">{t.utr || "—"}</div>
                                                <div className="text-slate-400 text-[10px]">{t.txn_id}</div>
                                            </td>
                                            <td className="px-3 py-2">
                                                {usernameEditing ? (
                                                    <span className="inline-flex items-center gap-1">
                                                        <input data-testid={`success-username-input-${t.txn_id}`} className="input !py-1 !text-xs w-32" value={ed.customer_username}
                                                               onChange={(e) => setEditValue(t.txn_id, "customer_username", e.target.value)} autoFocus />
                                                        <button data-testid={`success-username-save-${t.txn_id}`} type="button" onClick={() => saveField(t.txn_id, "customer_username")} disabled={saving} className="text-emerald-600 hover:bg-emerald-50 rounded p-1"><Check className="w-3.5 h-3.5" /></button>
                                                        <button type="button" onClick={() => cancelEdit(t.txn_id, "customer_username")} className="text-slate-400 hover:bg-slate-100 rounded p-1"><X className="w-3.5 h-3.5" /></button>
                                                    </span>
                                                ) : (
                                                    <button type="button" disabled={!perms.can_write}
                                                            data-testid={`success-username-edit-${t.txn_id}`}
                                                            onClick={() => startEdit(t.txn_id, "customer_username", t.customer_username)}
                                                            className={`inline-flex items-center gap-1 ${perms.can_write ? "hover:text-blue-600 cursor-pointer" : "cursor-default"}`}>
                                                        <span>{t.customer_username || "—"}</span>
                                                        {perms.can_write && <Pencil className="w-3 h-3 opacity-40" />}
                                                    </button>
                                                )}
                                            </td>
                                            <td className="px-3 py-2 font-semibold">{fmtINR(t.amount)}</td>
                                            <td className="px-3 py-2 text-xs">
                                                {linkEditing ? (
                                                    <span className="inline-flex items-center gap-1">
                                                        <select className="input !py-1 !text-xs w-32"
                                                                value={ed.link_code || ""}
                                                                onChange={(e) => setEditValue(t.txn_id, "link_code", e.target.value)}>
                                                            <option value="">—</option>
                                                            {linkOptions.map((o) => <option key={o.code} value={o.code}>{o.name}</option>)}
                                                        </select>
                                                        <button type="button" onClick={() => saveField(t.txn_id, "link_code")} disabled={saving} className="text-emerald-600 hover:bg-emerald-50 rounded p-1"><Check className="w-3.5 h-3.5" /></button>
                                                        <button type="button" onClick={() => cancelEdit(t.txn_id, "link_code")} className="text-slate-400 hover:bg-slate-100 rounded p-1"><X className="w-3.5 h-3.5" /></button>
                                                    </span>
                                                ) : (
                                                    <button type="button" disabled={!perms.can_write}
                                                            data-testid={`success-link-edit-${t.txn_id}`}
                                                            onClick={() => startEdit(t.txn_id, "link_code", t.link_code)}
                                                            className={`inline-flex items-center gap-1 ${perms.can_write ? "hover:text-blue-600 cursor-pointer" : "cursor-default"}`}>
                                                        <span>{t.link_display_name || t.link_code || "—"}</span>
                                                        {perms.can_write && <Pencil className="w-3 h-3 opacity-40" />}
                                                    </button>
                                                )}
                                            </td>
                                            {/* Website tag — read-only here.
                                                Stamped at /pay/{code}/initiate time from the customer's
                                                checkout chooser. Shows the historical name snapshot
                                                so renames/deletions don't rewrite past txns. */}
                                            <td data-testid={`success-website-${t.txn_id}`} className="px-3 py-2 text-xs">
                                                {t.website_name ? (
                                                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md border text-[11px] font-semibold truncate max-w-[160px] ${ws.pill}`} title={t.website_name}>
                                                        {ws.initial && (
                                                            <span className="font-mono text-[9px] tracking-wider opacity-70 border-r pr-1 mr-0.5 border-current/30">
                                                                {ws.initial}
                                                            </span>
                                                        )}
                                                        {t.website_name}
                                                    </span>
                                                ) : (
                                                    <span className="text-slate-300 text-[11px]">—</span>
                                                )}
                                            </td>
                                            <td className="px-3 py-2 text-center">
                                                <input data-testid={`success-credited-${t.txn_id}`} type="checkbox"
                                                       checked={!!t.credited_to_wallet}
                                                       disabled={!perms.can_write || saving}
                                                       onChange={() => toggleCredited(t)}
                                                       className="w-4 h-4 cursor-pointer disabled:cursor-not-allowed accent-emerald-600" />
                                            </td>
                                            <td className="px-3 py-2 text-xs">
                                                {bonusVEditing ? (
                                                    <span className="inline-flex items-center gap-1">
                                                        <input data-testid={`success-bonusv-input-${t.txn_id}`} type="number" min="0" step="0.01" className="input !py-1 !text-xs w-20" value={ed.bonus_value}
                                                               onChange={(e) => setEditValue(t.txn_id, "bonus_value", e.target.value)} autoFocus />
                                                        <button data-testid={`success-bonusv-save-${t.txn_id}`} type="button" onClick={() => saveField(t.txn_id, "bonus_value")} disabled={saving} className="text-emerald-600 hover:bg-emerald-50 rounded p-1"><Check className="w-3.5 h-3.5" /></button>
                                                        <button type="button" onClick={() => cancelEdit(t.txn_id, "bonus_value")} className="text-slate-400 hover:bg-slate-100 rounded p-1"><X className="w-3.5 h-3.5" /></button>
                                                    </span>
                                                ) : (
                                                    <button type="button" disabled={!perms.can_write}
                                                            data-testid={`success-bonusv-edit-${t.txn_id}`}
                                                            onClick={() => startEdit(t.txn_id, "bonus_value", t.bonus_value)}
                                                            className={`inline-flex items-center gap-1 ${perms.can_write ? "hover:text-blue-600 cursor-pointer" : "cursor-default"}`}>
                                                        <span>{t.bonus_value != null ? fmtINR(t.bonus_value) : "—"}</span>
                                                        {perms.can_write && <Pencil className="w-3 h-3 opacity-40" />}
                                                    </button>
                                                )}
                                            </td>
                                            <td className="px-3 py-2 text-xs">
                                                {bonusPEditing ? (
                                                    <span className="inline-flex items-center gap-1">
                                                        <input data-testid={`success-bonusp-input-${t.txn_id}`} type="number" min="0" max="100" step="0.01" className="input !py-1 !text-xs w-16" value={ed.bonus_percent}
                                                               onChange={(e) => setEditValue(t.txn_id, "bonus_percent", e.target.value)} autoFocus />
                                                        <button data-testid={`success-bonusp-save-${t.txn_id}`} type="button" onClick={() => saveField(t.txn_id, "bonus_percent")} disabled={saving} className="text-emerald-600 hover:bg-emerald-50 rounded p-1"><Check className="w-3.5 h-3.5" /></button>
                                                        <button type="button" onClick={() => cancelEdit(t.txn_id, "bonus_percent")} className="text-slate-400 hover:bg-slate-100 rounded p-1"><X className="w-3.5 h-3.5" /></button>
                                                    </span>
                                                ) : (
                                                    <button type="button" disabled={!perms.can_write}
                                                            data-testid={`success-bonusp-edit-${t.txn_id}`}
                                                            onClick={() => startEdit(t.txn_id, "bonus_percent", t.bonus_percent)}
                                                            className={`inline-flex items-center gap-1 ${perms.can_write ? "hover:text-blue-600 cursor-pointer" : "cursor-default"}`}>
                                                        <span>{t.bonus_percent != null ? `${t.bonus_percent}%` : "—"}</span>
                                                        {perms.can_write && <Pencil className="w-3 h-3 opacity-40" />}
                                                    </button>
                                                )}
                                            </td>
                                            <td className="px-3 py-2 text-[11px] text-slate-500">{t.created_at ? new Date(t.created_at).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" }) : "—"}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {!loading && items.length > 0 && (
                <div className="text-[11px] text-slate-500 text-right">
                    Showing {items.length} success transaction{items.length === 1 ? "" : "s"}.
                    {!perms.can_write && <span className="ml-1 text-amber-700">Read-only access.</span>}
                </div>
            )}
        </div>
    );
}
